import React from 'react';
import { UnitToggle } from '../../../components/common/UnitToggle';
import { WeightInput } from '../../../components/common/WeightInput';
import type { WeightUnit } from '../../../hooks/useWeightUnit';
import { useLanguage } from '../../../features/language/useLanguage';

interface RoastWeightAnalysisSectionProps {
  showTargetLabels: boolean;
  unit: WeightUnit;
  onToggle?: (unit: WeightUnit) => void;
  weightInput: number | undefined;
  calculatedWeight: number;
  onWeightInputChange: (value: number | undefined) => void;
  weightOutput: number | undefined;
  onWeightOutputChange: (value: number | undefined) => void;
  shrinkage: { percent: string | number; weight: string | number };
  readOnly: boolean;
  disableWeightInput: boolean;
  showInputDiffWarning?: boolean;
}

export const RoastWeightAnalysisSection: React.FC<RoastWeightAnalysisSectionProps> = ({
  showTargetLabels,
  unit,
  onToggle,
  weightInput,
  calculatedWeight,
  onWeightInputChange,
  weightOutput,
  onWeightOutputChange,
  shrinkage,
  readOnly,
  disableWeightInput,
  showInputDiffWarning = false,
}) => {
  const { t } = useLanguage();
  const formatWeight = (val: number | undefined) => {
    if (val === undefined || isNaN(val)) return t('settingsMcp.values.none');
    if (unit === 'kg') return val.toFixed(3);
    return String(Math.round(val * 1000));
  };

  return (
    <div className="bg-white border rounded-lg p-4 space-y-4">
      <h4 className="text-sm font-medium text-gray-700">
        {showTargetLabels ? t('roast.weightAnalysis.targetWeightAnalysis') : t('roast.weightAnalysis.weightAnalysis')}
      </h4>

      <div>
        <div className="flex justify-between items-center mb-1">
          <label className="block text-xs text-gray-500">{t('workOrders.table.roastInput')}</label>
          {onToggle && <UnitToggle unit={unit} onToggle={onToggle} />}
        </div>
        <WeightInput
          value={weightInput}
          onChange={onWeightInputChange}
          unit={unit}
          disabled={readOnly || disableWeightInput}
          className="block w-full text-sm border-gray-300 rounded-md border p-1.5"
          placeholder={t('inventory.history.placeholderKg')}
        />

        {showInputDiffWarning &&
          weightInput !== undefined &&
          weightInput > 0 &&
          Math.abs(weightInput - calculatedWeight) > 0.0001 && (
          <p className="text-xs text-amber-600 mt-1">
            {t('roast.weightAnalysis.differsCalculated', { weight: formatWeight(calculatedWeight), unit })}
          </p>
        )}
      </div>

      <div>
        <div className="flex justify-between items-center mb-1">
          <label className="block text-xs text-gray-500">{t('workOrders.table.roastOutput')}</label>
          {onToggle && <UnitToggle unit={unit} onToggle={onToggle} />}
        </div>
        <WeightInput
          value={weightOutput}
          onChange={onWeightOutputChange}
          unit={unit}
          disabled={readOnly}
          className="block w-full text-sm border-gray-300 rounded-md border p-1.5"
          placeholder={t('inventory.history.placeholderKg')}
        />
        {weightOutput !== undefined &&
          weightInput !== undefined &&
          Number(weightOutput) > 0 &&
          (weightInput - Number(weightOutput)) > weightInput * 0.25 && (
            <p className="text-xs text-red-600 mt-1">{t('roast.weightAnalysis.largeShrinkage')}</p>
          )}

        {weightOutput !== undefined &&
          weightInput !== undefined &&
          Number(weightOutput) > Number(weightInput) && (
            <p className="text-xs text-red-600 mt-1">{t('roast.weightAnalysis.outputGreater')}</p>
          )}
      </div>

      <div className="pt-2 border-t">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">{t('reports.roasting.lossPercent')}:</span>
          <span className="font-medium">
            {shrinkage.percent}% ({formatWeight(Number(shrinkage.weight))} {unit})
          </span>
        </div>
      </div>
    </div>
  );
};
